import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  CircularProgress,
  Box,
  Typography,
  AutocompleteRenderInputParams
} from '@mui/material';
import { StyledAutocomplete, TextFieldStyled } from './styles';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { SvgIconProps } from '@mui/material/SvgIcon';
import { OptionType, PaginatedSearchSelectProps } from '@/interfaces/paginatedSearchSelect.interface';

// Custom Dropdown Icon
const CustomDropdownIcon: React.FC<SvgIconProps> = (props) => (
  <ArrowDropDownIcon sx={{ color: '#5b8c6f' }} {...props} />
);

// Custom Popper to bypass Popper.js calculations entirely for Zoom compatibility
const CustomPopper = (props: any) => {
  // Explicitly destructure 'style' to prevent MUI's calculated styles (top/left) from interfering
  const { disablePortal, anchorEl, open, placement, modifiers, style, ...other } = props;

  if (!open) return null;

  return (
    <Box
      {...other}
      sx={{
        position: 'absolute !important',
        top: '100% !important',
        left: '0 !important',
        right: '0 !important',
        width: '100% !important',
        transform: 'none !important',
        zIndex: 1300,
        marginTop: '4px',
        ...props.sx,
      }}
    />
  );
};

const PaginatedSearchSelect = <T extends OptionType>({
  fetchOptions,
  getOptionLabel,
  value,
  onChange,
  label = "Select an Item...",
  initialInputValue = "",
  placeholder,
  size = 'small',
  ...rest
}: React.PropsWithChildren<PaginatedSearchSelectProps<T>>): React.ReactElement => {

  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<T[]>(Array.isArray(value) ? value : (value ? [value] : []));
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [inputValue, setInputValue] = useState<string>(initialInputValue);

  const valueRef = useRef(value);
  const lastFetchedPageRef = useRef(0);
  const isFetchingRef = useRef(false);
  const currentQueryRef = useRef(inputValue);
  const hasMoreRef = useRef(true); // Stable source of truth for loading logic

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  // --- Data Fetching ---
  const loadData = useCallback(async (searchQuery: string, pageNumber: number, reset: boolean = false) => {
    // If resetting (e.g. search or initial open), update currentQueryRef
    if (reset) {
      currentQueryRef.current = searchQuery;
    }

    // Prevent redundant fetches
    // 1. If not resetting and we already have this page or no more data
    if (!reset && (!hasMoreRef.current || pageNumber <= lastFetchedPageRef.current)) return;
    // 2. If we are already fetching and it's not a reset
    if (isFetchingRef.current && !reset) return;

    isFetchingRef.current = true;
    setLoading(true);

    try {
      const response = await fetchOptions(searchQuery, pageNumber);

      // Discard results if the query has changed since the request started
      if (searchQuery !== currentQueryRef.current) return;

      setOptions(prev => {
        const incomingOptions = response.options || [];
        const newOptions = reset ? incomingOptions : [...prev, ...incomingOptions];

        // Ensure selected value is preserved if not in new list
        const currentValue = valueRef.current;
        if (currentValue) {
          const values = Array.isArray(currentValue) ? currentValue : [currentValue];
          const missingValues = values.filter(val => !newOptions.some(opt => opt.id === val.id));
          newOptions.unshift(...missingValues);
        }

        // Remove duplicates based on ID (robust O(N) using Map with string keys)
        const uniqueMap = new Map();
        newOptions.forEach(opt => {
          // Use String() for key to avoid number/string mismatch issues
          // and ensure we skip null/undefined items correctly
          if (opt && opt.id != null) {
            const key = String(opt.id);
            if (!uniqueMap.has(key)) {
              uniqueMap.set(key, opt);
            }
          }
        });
        return Array.from(uniqueMap.values());
      });

      setHasMore(response.hasMore);
      hasMoreRef.current = response.hasMore;
      setPage(pageNumber);
      lastFetchedPageRef.current = pageNumber;
    } catch (error) {
      console.error("Error fetching options:", error);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [fetchOptions]); // hasMore removed from deps to keep loadData stable

  // --- Effects ---

  // 1. Debounced Search (runs when inputValue changes)
  useEffect(() => {
    if (!open) return;

    // Use a Ref to check if the inputValue actually changed compared 
    // to what was last searched/loaded to prevent redundant resets.
    if (inputValue === currentQueryRef.current && lastFetchedPageRef.current > 0) return;

    const timer = setTimeout(() => {
      loadData(inputValue, 1, true);
    }, 400);
    return () => clearTimeout(timer);
  }, [inputValue, loadData, open]);
  // keeping 'open' in dependency is safer for linter, and logic handles 'if (!open) return'

  // 2. Handle Open/Close Actions
  useEffect(() => {
    if (open) {
      // Immediate fetch when triggering open
      // We pass the current inputValue to search with it (or empty string)
      loadData(inputValue, 1, true);
    } else {
      // Reset state when closed
      setOptions(Array.isArray(value) ? value : (value ? [value] : []));
      setPage(1);
      lastFetchedPageRef.current = 0;
      setHasMore(true);
      hasMoreRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]); // Only trigger this effect when 'open' state changes
  // We exclude 'inputValue' and 'value' intentionally to avoid double-fetching or resetting during typing

  // 3. Sync input value with selection
  useEffect(() => {
    if (value) {
      if (Array.isArray(value)) {
        if (!open) setInputValue("");
      } else {
        const labelStr = (value[getOptionLabel] as string) || '';
        if (labelStr !== inputValue && !open) {
          setInputValue(labelStr);
        }
      }
    } else if (!open && !value) {
      setInputValue("");
    }
  }, [value, open, getOptionLabel, inputValue]);


  // --- Handlers ---
  const handleScroll = (event: React.UIEvent<HTMLUListElement>) => {
    const listboxNode = event.currentTarget;
    if (loading || !hasMore) return;

    // Trigger when scrolled to bottom (with small buffer)
    if (listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 20) {
      loadData(inputValue, page + 1, false);
    }
  };

  // Wrapper Box ensures a strictly established coordinate system / containing block for the absolute Popper
  return (
    <Box sx={{ position: 'relative', width: rest.fullWidth ? '100%' : 'auto', display: 'inline-block' }}>
      <StyledAutocomplete
        {...rest}
        disablePortal

        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}

        PopperComponent={CustomPopper}

        // Value & Input Control
        value={value}
        onChange={(event: React.SyntheticEvent, newValue: any) => onChange(newValue)}
        inputValue={inputValue}
        onInputChange={(event: React.SyntheticEvent, newInputValue: string) => setInputValue(newInputValue)}

        // Options
        options={options}
        getOptionLabel={(option: T) => (option[getOptionLabel] as string) || ''}
        isOptionEqualToValue={(option: T, val: T) => option.id === val.id}
        filterOptions={(x: T[]) => x} // Disable client-side filtering

        loading={loading}
        popupIcon={<CustomDropdownIcon />}

        renderInput={(params: AutocompleteRenderInputParams) => (
          <TextFieldStyled
            {...params}
            placeholder={placeholder}
            size={size}
            error={rest.error}
            helperText={rest.helperText}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {loading && <CircularProgress color="inherit" size={20} />}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
        )}

        ListboxProps={{
          onScroll: handleScroll
        }}

        renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: T) => (
          <Box component="li" {...props} key={option.id}>
            <Typography noWrap>{option[getOptionLabel] as string}</Typography>
          </Box>
        )}
      />
    </Box>
  );
};

export default PaginatedSearchSelect;
